import { z } from 'zod';
import { CursorInputSchema, IdSchema, pageOf, withMeta } from '../common';
import { NotifSchema } from '../dataset/core';
import { defineProcedures, mutation, query, type RouterShape } from '../procedure';
import { defineFamily } from './family';

/** notifications family — `manage.notifications.*`: feed with class-0 emphasis, per-category × channel matrix, quiet hours
 *  (defer-and-digest), badge parity across devices (SDD §7.4 Notifications row). */

export const NotificationItemSchema = NotifSchema.extend({
  id: IdSchema,
  /** Class 0 = safety-critical (lockouts, breaches) — never deferred by quiet hours. */
  classZero: z.boolean(),
});
export type NotificationItem = z.infer<typeof NotificationItemSchema>;
export const NotificationFeedSchema = pageOf(NotificationItemSchema).extend({
  unreadCount: z.number().int().nonnegative(),
});
export const NotificationChannelSchema = z.enum(['inApp', 'push', 'email']);
export const NotificationPrefsSchema = withMeta({
  /** category key → channel → enabled. */
  matrix: z.record(z.string(), z.record(NotificationChannelSchema, z.boolean())),
  categories: z.array(z.object({ key: z.string(), label: z.string(), classZero: z.boolean() }).strict()),
  quietHours: z
    .object({ enabled: z.boolean(), start: z.string(), end: z.string(), timezone: z.string() })
    .strict(),
  /** Preview of what quiet hours would defer into the digest right now. */
  deferPreview: z.array(z.string()),
});

export const notificationsProcedures = defineProcedures({
  'manage.notifications.feed': query(
    'Notification feed (cursor) + unread badge',
    CursorInputSchema.optional(),
    NotificationFeedSchema,
  ),
  'manage.notifications.markRead': mutation(
    'Mark items (or all) read',
    z.object({ ids: z.array(IdSchema).max(200).optional(), all: z.boolean().optional() }).strict(),
    withMeta({ unreadCount: z.number().int().nonnegative() }),
  ),
  'manage.notifications.prefs': query(
    'Category × channel matrix + quiet hours',
    z.void(),
    NotificationPrefsSchema,
  ),
  'manage.notifications.updatePrefs': mutation(
    'Update the matrix / quiet hours',
    z
      .object({
        matrix: z.record(z.string(), z.partialRecord(NotificationChannelSchema, z.boolean())).optional(),
        quietHours: z
          .object({ enabled: z.boolean(), start: z.string(), end: z.string(), timezone: z.string() })
          .strict()
          .optional(),
      })
      .strict(),
    NotificationPrefsSchema,
  ),
});
export type NotificationsFamilyClient = RouterShape<typeof notificationsProcedures>;

export const notificationsFamily = defineFamily({
  name: 'notifications',
  title: 'Notifications',
  screens: ['Notifications'],
  procedures: notificationsProcedures,
  binding: 'simulated',
});
